import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @file ChecklistField.tsx
 * @description checklist 字段类型的紧凑表格渲染器。
 *
 * 数据形态：
 *   value: { [clauseId]: { result: 'yes'|'no'|'na'|null, note: string, attachments: number[] } }
 *
 * UI 结构：
 *   - 顶部 field.label
 *   - 按 groups 分块，组内一行一 clause
 *   - 每行列：编号 / 条文（含 ★★） / 結果 radio / 意見 textarea / 附件 upload
 *   - 列开关由 field.requireResult/requireNote/requireAttachment 控制
 *   - 条文 / 组说明经 ClauseText 渲染：（★★）标红、必查核条文黑色粗体、换行 / (1)(2) 自动分段
 *   - clause.kind='parent'（主条目）/ 'category'（分类标题）只显示文字，不渲染填写栏位
 *
 * 上传：
 *   附件列的真实上传由 props.uploadHandlers 注入（forms 包不直接 import OSS 逻辑）。
 *   未传 uploadHandlers 时附件列退化为只读展示（无法选取新文件）。
 */
import { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Input, Radio, Typography, Upload, Button, message } from 'antd';
import { Camera } from 'lucide-react';
import { readClause, writeClause } from './checklist-data';
import { ClauseText } from './ClauseText';
const RESULT_OPTIONS = [
    { value: 'yes', label: '是' },
    { value: 'no', label: '否' },
    { value: 'na', label: '不適用' },
];
// 注：EMPTY_CLAUSE / readClause / writeClause 已抽至 ./checklist-data（纯模块，便于单测引用稳定性）。
export const ChecklistField = memo(function ChecklistField({ field, value, onChange, disabled, uploadHandlers, }) {
    // filename cache：用户当前会话上传的 file id → filename，作为 uploadHandlers.fileMetas 的同会话兜底。
    // 当宿主还没把刚上传的 id 拉回 fileMetas 时（典型场景：上传后未 invalidate），用此缓存先行展示。
    // 该 state 不持久化（刷新后只剩 file id），刷新后由 fileMetas 接力。
    const [filenameCache, setFilenameCache] = useState({});
    // 阶段 3：fileId → blob URL 缓存（供 antd Upload 的预览/下载链接）
    const [urlCache, setUrlCache] = useState({});
    // ── value/onChange ref：让所有 clause 级回调引用恒定 ──
    // 否则每敲一个字 value 换引用 → patchClause 等回调换引用 → 打穿 ChecklistRow 的 memo，
    // 导致在某一行的意见栏输入会重渲染全部上百行（卡顿主因）。
    const valueRef = useRef(value);
    valueRef.current = value;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    /** 写回单条 clause（读 ref 中最新 value，引用恒定） */
    const patchClause = useCallback((clauseId, patch) => {
        onChangeRef.current(writeClause(valueRef.current, clauseId, patch));
    }, []);
    /** 上传成功：缓存文件名 + 把新 id 追加到 clause.attachments */
    const appendAttachment = useCallback((clauseId, fileId, filename) => {
        setFilenameCache((prev) => ({ ...prev, [fileId]: filename }));
        const cv = readClause(valueRef.current, clauseId);
        onChangeRef.current(writeClause(valueRef.current, clauseId, { attachments: [...cv.attachments, fileId] }));
    }, []);
    /** 删除附件：从 clause.attachments 移除该 id，并通知宿主（用于失效 fileMetas 缓存等） */
    const removeAttachment = useCallback((clauseId, fileId) => {
        const cv = readClause(valueRef.current, clauseId);
        onChangeRef.current(writeClause(valueRef.current, clauseId, {
            attachments: cv.attachments.filter((id) => id !== fileId),
        }));
        uploadHandlers?.onRemoveFile?.(fileId);
    }, [uploadHandlers]);
    // ── 所有 attachment id 的稳定字符串 key ──
    // 只有「附件集合」变化才重解析 blob URL；意见栏每敲一个字（value 变但附件未变）不再触发，
    // 避免每次 keystroke 都遍历全部 clause 跑一遍异步 resolveBlobUrl。
    const attachmentKey = useMemo(() => {
        const ids = [];
        for (const group of field.groups) {
            for (const clause of group.clauses) {
                const cv = readClause(value, clause.id);
                for (const id of cv.attachments)
                    ids.push(id);
            }
        }
        return Array.from(new Set(ids))
            .sort((a, b) => a - b)
            .join(',');
    }, [field.groups, value]);
    useEffect(() => {
        if (!uploadHandlers?.resolveBlobUrl)
            return;
        if (!attachmentKey)
            return;
        let cancelled = false;
        const allIds = attachmentKey.split(',').map(Number);
        void (async () => {
            const next = {};
            for (const id of allIds) {
                if (urlCache[id]) {
                    next[id] = urlCache[id];
                    continue;
                }
                const url = await uploadHandlers.resolveBlobUrl?.(id);
                if (url)
                    next[id] = url;
            }
            if (!cancelled)
                setUrlCache((prev) => ({ ...prev, ...next }));
        })();
        return () => {
            cancelled = true;
        };
        // 依赖 attachmentKey（附件集合字符串）而非整个 value；不依赖 urlCache 防循环。
    }, [attachmentKey, uploadHandlers]);
    /** 表格總欄數（編號 + 條文 + 可選三欄），供主條目 / 分類標題行計算 colSpan */
    const columnCount = 2 +
        (field.requireResult ? 1 : 0) +
        (field.requireNote ? 1 : 0) +
        (field.requireAttachment ? 1 : 0);
    return (_jsx("div", { className: "space-y-6", children: field.groups.map((group) => (_jsxs("div", { style: { marginBottom: 24 }, children: [group.title && (_jsx(Typography.Title, { level: 5, style: { margin: '12px 0 8px' }, children: group.title })), group.subTitle && (_jsx("div", { style: { marginBottom: 8, fontSize: 13, color: 'rgba(0,0,0,0.65)' }, children: _jsx(ClauseText, { text: group.subTitle, gap: 4 }) })), _jsxs("table", { className: "w-full border-collapse", style: {
                        fontSize: 12,
                        tableLayout: 'fixed',
                        borderTop: '1px solid #d9d9d9',
                        borderLeft: '1px solid #d9d9d9',
                        borderRight: '1px solid #d9d9d9',
                    }, children: [_jsx("thead", { children: _jsxs("tr", { style: { background: '#fafafa', borderBottom: '2px solid #d9d9d9' }, children: [_jsx("th", { style: {
                                            width: '6%',
                                            padding: '8px 6px',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            borderRight: '1px solid #e8e8e8',
                                        }, children: field.columnLabels?.code ?? '編號' }), _jsx("th", { style: {
                                            width: field.requireResult && field.requireNote && field.requireAttachment
                                                ? '38%'
                                                : field.requireNote
                                                    ? '50%'
                                                    : '60%',
                                            padding: '8px 6px',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderRight: '1px solid #e8e8e8',
                                        }, children: field.columnLabels?.text ?? '查核條文' }), field.requireResult && (_jsx("th", { style: {
                                            width: '16%',
                                            padding: '8px 6px',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            borderRight: '1px solid #e8e8e8',
                                        }, children: field.columnLabels?.result ?? '確認' })), field.requireNote && (_jsx("th", { style: {
                                            width: '28%',
                                            padding: '8px 6px',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderRight: '1px solid #e8e8e8',
                                        }, children: field.columnLabels?.note ?? '意見/描述' })), field.requireAttachment && (_jsx("th", { style: {
                                            width: '12%',
                                            padding: '8px 6px',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                        }, children: field.columnLabels?.attachment ?? '附件' }))] }) }), _jsx("tbody", { children: group.clauses.map((clause) => 
                            // 主條目 / 分類標題：只顯示文字，不渲染 確認/意見/上傳 欄位（序60/61/62）
                            clause.kind === 'parent' || clause.kind === 'category' ? (_jsx(ChecklistStructuralRow, { clause: clause, colSpan: columnCount }, clause.id)) : (_jsx(ChecklistRow, { clause: clause, cv: readClause(value, clause.id), requireResult: !!field.requireResult, requireNote: !!field.requireNote, requireAttachment: !!field.requireAttachment, disabled: disabled, uploadHandlers: uploadHandlers, filenameCache: filenameCache, urlCache: urlCache, patchClause: patchClause, appendAttachment: appendAttachment, removeAttachment: removeAttachment }, clause.id))) })] })] }, group.id))) }));
});
/**
 * 主條目（kind='parent'）/ 分類標題（kind='category'）行：不收集任何填寫資料。
 *  - parent：編號欄 + 條文欄（條文欄橫跨其餘欄位），如「4.1 優先採取下列預防措施：」；
 *  - category：整列橫跨、粗體，如「健康管理」「2.1 生產環境條件與相鄰土地之使用」。
 */
const ChecklistStructuralRow = memo(function ChecklistStructuralRow({ clause, colSpan, }) {
    if (clause.kind === 'category') {
        // 巢狀分類（如 1.2 → 1.2.1）依編號層級縮排：兩段編號不縮，每多一段縮 16px
        const depth = clause.code ? clause.code.split('.').length : 2;
        return (_jsx("tr", { style: { borderBottom: '1px solid #d9d9d9', background: '#f7f8fa' }, children: _jsx("td", { colSpan: colSpan, style: {
                    padding: '8px 6px',
                    paddingLeft: 6 + Math.max(0, depth - 2) * 16,
                    fontWeight: 600,
                    fontSize: 13,
                    borderRight: '1px solid #e8e8e8',
                }, children: _jsx(ClauseText, { text: clause.code ? `${clause.code} ${clause.text}` : clause.text, mustAudit: clause.mustAudit }) }) }));
    }
    return (_jsxs("tr", { style: { borderBottom: '1px solid #d9d9d9' }, children: [_jsx("td", { style: {
                    padding: '8px 6px',
                    verticalAlign: 'top',
                    textAlign: 'center',
                    fontFamily: 'monospace',
                    borderRight: '1px solid #e8e8e8',
                }, children: clause.code }), _jsx("td", { colSpan: Math.max(1, colSpan - 1), style: { padding: '8px 6px', verticalAlign: 'top', borderRight: '1px solid #e8e8e8' }, children: _jsx(ClauseText, { text: clause.text, mustAudit: clause.mustAudit }) })] }));
});
const ChecklistRow = memo(function ChecklistRow({ clause, cv, requireResult, requireNote, requireAttachment, disabled, uploadHandlers, filenameCache, urlCache, patchClause, appendAttachment, removeAttachment, }) {
    // 把 attachments id[] 渲染成 antd Upload 期望的 fileList 形态。
    // 文件名解析顺序：fileMetas（持久化，跨刷新有效） → filenameCache（同会话兜底） → file#${id}（最后兜底）
    const fileList = cv.attachments.map((id) => ({
        uid: String(id),
        name: uploadHandlers?.fileMetas?.get(id)?.filename ?? filenameCache[id] ?? `file#${id}`,
        status: 'done',
        url: urlCache[id],
    }));
    // antd Upload 的 customRequest：拿到 File → 走 uploadHandlers.onUploadFile → 拿到 file id → 写回 attachments
    const customRequest = async ({ file, onSuccess, onError, }) => {
        if (!uploadHandlers) {
            onError?.(new Error('上传通道未配置'));
            return;
        }
        try {
            const uploaded = await uploadHandlers.onUploadFile(file);
            appendAttachment(clause.id, uploaded.id, uploaded.filename);
            onSuccess?.({ id: uploaded.id });
        }
        catch (err) {
            message.error('上傳失敗');
            onError?.(err);
        }
    };
    return (_jsxs("tr", { style: { borderBottom: '1px solid #d9d9d9' }, children: [_jsx("td", { style: {
                    padding: '8px 6px',
                    verticalAlign: 'top',
                    textAlign: 'center',
                    fontFamily: 'monospace',
                    borderRight: '1px solid #e8e8e8',
                }, children: clause.code }), _jsx("td", { style: {
                    padding: '8px 6px',
                    verticalAlign: 'top',
                    borderRight: '1px solid #e8e8e8',
                }, children: _jsx(ClauseText, { text: clause.text, mustAudit: clause.mustAudit }) }), requireResult && (_jsx("td", { style: {
                    padding: '8px 6px',
                    verticalAlign: 'top',
                    textAlign: 'center',
                    borderRight: '1px solid #e8e8e8',
                }, children: _jsx(Radio.Group, { value: cv.result, onChange: (e) => patchClause(clause.id, { result: e.target.value }), disabled: disabled, options: RESULT_OPTIONS, optionType: "button", size: "small" }) })), requireNote && (_jsx("td", { style: {
                    padding: '8px 6px',
                    verticalAlign: 'top',
                    borderRight: '1px solid #e8e8e8',
                }, children: _jsx(Input.TextArea, { autoSize: { minRows: 1, maxRows: 6 }, value: cv.note, onChange: (e) => patchClause(clause.id, { note: e.target.value }), disabled: disabled, maxLength: 2000 }) })), requireAttachment && (_jsx("td", { style: {
                    padding: '8px 6px',
                    verticalAlign: 'top',
                    textAlign: 'center',
                }, children: _jsx(Upload, { disabled: disabled || !uploadHandlers, multiple: true, fileList: fileList, customRequest: uploadHandlers
                        ? (options) => void customRequest(options)
                        : undefined, onRemove: (file) => {
                        removeAttachment(clause.id, Number(file.uid));
                        return true;
                    }, showUploadList: {
                        showRemoveIcon: !disabled,
                        showPreviewIcon: false,
                    }, children: _jsx(Button, { size: "small", icon: _jsx(Camera, { size: 12 }), disabled: !uploadHandlers, children: "\u4E0A\u50B3" }) }) }))] }));
});
//# sourceMappingURL=ChecklistField.js.map